import { ChangeDetectionStrategy, Component, effect, OnDestroy, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { ChatRetention, UserSettingsStore } from '../../../../state/user-settings.repository';
import { MessageStore } from '../../../../state/messages.repository';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { MatToggleWrapper } from '../../../../shared/components/material-wrappers/mat-toggle-wrapper/mat-toggle-wrapper';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { marker as _ } from '@vandres/ngx-translate-extract-marker';
import { faBell, faGear, faInfoCircle, faNetworkWired } from '@fortawesome/pro-duotone-svg-icons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { isTauri } from '../../../../shared/helpers/platform';
import { Tab, Tabs } from '../../../../shared/components/ui/tabs/tabs';
import { faTableLayout } from '@fortawesome/duotone-regular-svg-icons/faTableLayout';
import { TooltipDirective } from '../../../../shared/components/ui/tooltip/tooltip.directive';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialog,
  ConfirmDialogData,
} from '../../../../shared/components/ui/confirm-dialog/confirm-dialog';
import { SquareIconBtn } from '../../../../shared/components/ui/buttons/square-icon-btn/square-icon-btn';
import { LanguageSelect } from '../language-select/language-select';

@Component({
  selector: 'andor-settings-general',
  standalone: true,
  imports: [
    MatSlideToggle,
    TranslatePipe,
    FontAwesomeModule,
    MatToggleWrapper,
    AsyncPipe,
    Tabs,
    TooltipDirective,
    SquareIconBtn,
    LanguageSelect,
  ],
  templateUrl: './settings-general.html',
  styleUrl: './settings-general.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsGeneral implements OnDestroy {
  useWebRTC = signal(false);
  stopMicAccessOnMute = signal(true);
  protected readonly faBell = faBell;
  protected readonly faGear = faGear;
  protected readonly faInfoCircle = faInfoCircle;
  protected readonly faNetworkWired = faNetworkWired;
  private _destroy$: Subject<void> = new Subject<void>();
  protected readonly faTableLayout = faTableLayout;
  protected readonly faTrash = faTrash;
  protected readonly isTauri = isTauri;
  tabs: Tab[] = [
    {
      id: 'auto',
      label: _('auto'),
    },
    {
      id: '1',
      label: '1',
    },
    {
      id: '2',
      label: '2',
    },
    {
      id: '4',
      label: '4',
    },
    {
      id: '6',
      label: '6',
    },
    {
      id: '9',
      label: '9',
    },
  ];
  activeTab = signal<Tab>(this.tabs[0]);

  retentionTabs: Tab[] = [
    { id: 'off', label: _('Off') },
    { id: '1w', label: _('1 week') },
    { id: '1m', label: _('1 month') },
    { id: '1y', label: _('1 year') },
  ];
  activeRetentionTab = signal<Tab>(this.retentionTabs[2]);

  chatLayoutTabs: Tab[] = [
    { id: 'left', label: _('Left-aligned') },
    { id: 'split', label: _('Sided') },
  ];
  activeChatLayoutTab = signal<Tab>(this.chatLayoutTabs[0]);

  constructor(
    public userSettingsStore: UserSettingsStore,
    public translateService: TranslateService,
    private _messageStore: MessageStore,
    private _dialog: MatDialog
  ) {
    const userSettings = userSettingsStore.value;
    const activeTab = this.tabs.find((t) => t.id === userSettings.visibleTiles);
    this.activeTab.set(activeTab ?? this.tabs[0]);
    effect(() => {
      this.userSettingsStore.store.update((state) => {
        if (state.visibleTiles === this.activeTab().id) {
          return state;
        }
        return {
          ...state,
          visibleTiles: this.activeTab().id,
        };
      });
    });

    const activeRetentionTab = this.retentionTabs.find((t) => t.id === userSettings.chatRetention);
    this.activeRetentionTab.set(activeRetentionTab ?? this.retentionTabs[2]);
    effect(() => {
      const next = this.activeRetentionTab().id as ChatRetention;
      this.userSettingsStore.store.update((state) => {
        if (state.chatRetention === next) {
          return state;
        }
        return {
          ...state,
          chatRetention: next,
        };
      });
    });

    const activeChatLayoutTab = this.chatLayoutTabs.find((t) => t.id === userSettings.chatLayout);
    this.activeChatLayoutTab.set(activeChatLayoutTab ?? this.chatLayoutTabs[0]);
    effect(() => {
      const next = this.activeChatLayoutTab().id as 'left' | 'split';
      this.userSettingsStore.store.update((state) => {
        if (state.chatLayout === next) {
          return state;
        }
        return {
          ...state,
          chatLayout: next,
        };
      });
    });
    this.userSettingsStore.useWebRTC$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      this.useWebRTC.set(state);
    });

    this.userSettingsStore.stopMicAccessOnMute$
      .pipe(takeUntil(this._destroy$))
      .subscribe((state) => {
        this.stopMicAccessOnMute.set(state);
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
  }

  toggleAppSounds() {
    this.userSettingsStore.store.update((state) => ({
      ...state,
      playAppSounds: !state.playAppSounds,
    }));
  }

  toggleChatSounds() {
    this.userSettingsStore.store.update((state) => ({
      ...state,
      playChatSounds: !state.playChatSounds,
    }));
  }

  toggleJoinLeaveSounds() {
    this.userSettingsStore.store.update((state) => ({
      ...state,
      playJoinedLeftSounds: !state.playJoinedLeftSounds,
    }));
  }

  updateWebRTC() {
    this.userSettingsStore.store.update((state) => ({
      ...state,
      useWebRtc: !state.useWebRtc,
    }));
  }

  updateStopMicAccessOnMute() {
    this.userSettingsStore.store.update((state) => ({
      ...state,
      stopMicAccessOnMute: !state.stopMicAccessOnMute,
    }));
  }

  protected async deleteChatHistory(): Promise<void> {
    const data: ConfirmDialogData = {
      title: this.translateService.instant('Delete chat history?'),
      message: this.translateService.instant(
        'This permanently deletes all chat messages stored on this device, in every room. This cannot be undone.'
      ),
      confirmLabel: this.translateService.instant('Delete'),
      danger: true,
    };
    const ref = this._dialog.open(ConfirmDialog, {
      panelClass: 'confirm-dialog',
      backdropClass: 'confirm-backdrop',
      data,
    });
    const confirmed = await firstValueFrom(ref.afterClosed());
    if (!confirmed) return;
    this._messageStore.clear();
  }
}
